import { useEffect, useRef, useState } from 'react';
import { Twitter, ArrowRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const TwitterFeed = ({ handle = "primestatlab" }) => {
  const embedRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let timeout;

    const loadWidget = () => {
      if (window.twttr && window.twttr.widgets) {
        window.twttr.widgets.load(embedRef.current);
        // Check if the iframe rendered after a delay
        timeout = setTimeout(() => {
          const iframe = embedRef.current?.querySelector('iframe');
          if (iframe) {
            setLoaded(true);
          } else {
            setError(true);
          }
        }, 5000);
      } else {
        // Load the Twitter widget script
        const script = document.createElement('script');
        script.src = 'https://platform.twitter.com/widgets.js';
        script.async = true;
        script.charset = 'utf-8';
        script.onload = () => {
          if (window.twttr && window.twttr.widgets) {
            window.twttr.widgets.load(embedRef.current);
            timeout = setTimeout(() => {
              const iframe = embedRef.current?.querySelector('iframe');
              if (iframe) {
                setLoaded(true);
              } else {
                setError(true);
              }
            }, 5000);
          }
        };
        script.onerror = () => setError(true);
        document.head.appendChild(script);
      }
    };

    loadWidget();

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [handle]);

  const retryLoad = () => {
    setError(false);
    setLoaded(false);
    if (embedRef.current) {
      // Clear and re-add the anchor
      const anchor = embedRef.current.querySelector('.twitter-timeline');
      if (!anchor) {
        const a = document.createElement('a');
        a.className = 'twitter-timeline';
        a.setAttribute('data-theme', 'dark');
        a.setAttribute('data-height', '600');
        a.setAttribute('data-chrome', 'noheader nofooter noborders transparent');
        a.href = `https://twitter.com/${handle}`;
        a.textContent = `Tweets by @${handle}`;
        embedRef.current.prepend(a);
      }
      if (window.twttr && window.twttr.widgets) {
        window.twttr.widgets.load(embedRef.current);
        setTimeout(() => {
          const iframe = embedRef.current?.querySelector('iframe');
          if (iframe) {
            setLoaded(true);
            setError(false);
          } else {
            setError(true);
          }
        }, 5000);
      }
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="twitter-feed">
      <div className="flex items-center gap-2 mb-4">
        <Twitter className="text-[#1DA1F2]" size={20} />
        <h3 className="font-heading text-xl font-bold">LATEST FROM X</h3>
        <a 
          href={`https://x.com/${handle}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-[#1DA1F2] hover:underline font-mono text-xs uppercase tracking-wider flex items-center gap-1"
        >
          @{handle}
          <ArrowRight size={12} />
        </a>
      </div>

      {/* Embedded X Timeline */}
      <div ref={embedRef} className="mb-4" data-testid="twitter-embed-container">
        <a 
          className="twitter-timeline"
          data-theme="dark"
          data-height="600"
          data-chrome="noheader nofooter noborders transparent"
          href={`https://twitter.com/${handle}`}
        >
          Loading tweets by @{handle}...
        </a>
      </div>

      {/* Error/fallback state */}
      {error && (
        <div className="text-center py-4 mb-4 bg-surface rounded-lg border border-border" data-testid="twitter-feed-fallback">
          <p className="text-muted-foreground text-sm mb-3">
            Feed may take a moment to load. Click below to retry or view directly on X.
          </p>
          <div className="flex gap-2 justify-center">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={retryLoad}
              className="gap-1"
              data-testid="retry-feed-button"
            >
              <RefreshCw size={14} />
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Always show follow button */}
      <a 
        href={`https://x.com/${handle}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 bg-[#1DA1F2] text-white hover:bg-[#1DA1F2]/80 py-3 rounded font-mono text-sm uppercase tracking-wider transition-all"
        data-testid="follow-twitter"
      >
        <Twitter size={16} />
        Follow @{handle} on X
      </a>
    </div>
  );
};
